export interface AuditEvent {
  userId?: string;
  action: string;
  resource: string;
  resourceId?: string;
  timestamp: string;
  ipAddress?: string;
  userAgent?: string;
  status: 'success' | 'failure';
  details?: Record<string, unknown>;
}

/**
 * КРИТИЧНО: Логировать критичные действия
 * Требуется для SOC 2, GDPR compliance
 */
export async function logAuditEvent(event: Omit<AuditEvent, 'timestamp' | 'userAgent'>): Promise<void> {
  const auditEvent: AuditEvent = {
    ...event,
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
  };

  // Отправить на backend для хранения
  try {
    const { httpClient } = await import('../services/http-client');
    await httpClient.post('/audit-log', auditEvent);
    
    // Попытка отправить pending logs если они есть
    await sendPendingAuditLogs();
  } catch (error) {
    // Fallback: хранить локально если backend недоступен
    // Silently fail - endpoint может не существовать на бекенде

    // Сохранить в localStorage для последующей отправки
    try {
      const pendingLogs = JSON.parse(localStorage.getItem('pendingAuditLogs') || '[]');
      pendingLogs.push(auditEvent);
      localStorage.setItem('pendingAuditLogs', JSON.stringify(pendingLogs.slice(-50))); // Хранить только последние 50
    } catch (storageError) {
      console.error('Failed to save audit log to localStorage:', storageError);
    }
  }
}

/**
 * Отправить pending audit logs на backend
 */
async function sendPendingAuditLogs(): Promise<void> {
  try {
    const pendingLogsStr = localStorage.getItem('pendingAuditLogs');
    if (!pendingLogsStr) return;

    const pendingLogs: AuditEvent[] = JSON.parse(pendingLogsStr);
    if (pendingLogs.length === 0) return;

    const { httpClient } = await import('../services/http-client');
    
    // Отправить все pending logs
    await Promise.all(
      pendingLogs.map((log) => httpClient.post('/audit-log', log))
    );

    // Очистить после успешной отправки
    localStorage.removeItem('pendingAuditLogs');
  } catch {
    // Silently fail - endpoint может не существовать на бекенде
  }
}

// Примеры использования (для документации):

// Login
// logAuditEvent({
//   action: 'user.login',
//   resource: 'authentication',
//   status: 'success',
// });

// GDPR: Data export
// logAuditEvent({
//   userId: user.id,
//   action: 'data.export',
//   resource: 'user_data',
//   status: 'success',
//   details: { format: 'json' },
// });

// GDPR: Account deletion
// logAuditEvent({
//   userId: user.id,
//   action: 'account.delete',
//   resource: 'user',
//   resourceId: user.id,
//   status: 'success',
// });

// Finance: Payment
// logAuditEvent({
//   userId: user.id,
//   action: 'payment.process',
//   resource: 'payment',
//   resourceId: paymentId,
//   status: 'success',
//   details: { amount: 100, currency: 'USD' },
// });

// Access to sensitive data
// logAuditEvent({
//   userId: user.id,
//   action: 'data.access',
//   resource: 'user',
//   resourceId: otherUserId,
//   status: 'success',
// });

